import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import ChatShell from '../../components/Layout/ChatShell';
import Loader from '../../components/Loader';
import { ROUTES } from '../constants';

/**
 * Route-level code splitting.
 *
 * The whole app used to be one chunk — 3.24 MB raw, 952 kB gzipped, which CRA's own
 * build output flagged. A user landing on the sign-in screen downloaded and parsed the
 * video-calling stack, both charting libraries' worth of dependency graph, the settings
 * area and the notification feed before anything could paint.
 *
 * What stays EAGER is the landing experience and only that: `ChatShell`, the messages
 * sidebar and the conversation panes. Splitting those would trade the initial parse for
 * a network round-trip on the one route every session opens first, which is a worse deal.
 *
 * Everything else is lazy, grouped so a chunk maps onto an area of the product a user
 * navigates to as a unit. Imports are by PATH rather than through `containers/index.js`:
 * a barrel re-exports the whole set, so importing one name through it pulls all of them
 * into the same chunk and the split silently does nothing.
 *
 * `/calling` points at `CallRoute`, not `CallingAgora` — that module owns the
 * AgoraRTCProvider, which is what keeps the 1.55 MB RTC SDK out of the initial bundle.
 */

// Conversations — the landing route. Deliberately eager.
import Messages from '../../containers/Messages/Messages';
import MessagesSideBar from '../../containers/Messages/MessagesSideBar';
import ConversationPane from '../../containers/Messages/ConversationPane';

// Calls
const CallRoute = lazy(() => import('../../containers/Calling/CallRoute'));
const CallsSideBar = lazy(() => import('../../containers/Calls/CallsSideBar'));
const CallDetail = lazy(() => import('../../containers/Calls/CallDetail'));

// Saved messages
const SavedMessages = lazy(() => import('../../containers/Saved/SavedMessages'));
const SavedSideBar = lazy(() => import('../../containers/Saved/SavedSideBar'));

// Notes. Lazy for a specific reason beyond tidiness: this chunk carries TipTap and its
// ProseMirror dependencies, ~230 kB gzipped. Statically imported it would land in the
// initial bundle and every session would pay for it at boot, whether or not a note is ever
// opened — the same mistake the Agora SDK made before it was moved behind /calling.
const NotesSideBar = lazy(() => import('../../containers/Notes/NotesSideBar'));
const NoteDocument = lazy(() => import('../../containers/Notes/NoteDocument'));

// Notifications
const Notifications = lazy(() => import('../../containers/Notifications/Notifications'));
const NotificationsSideBar = lazy(
  () => import('../../containers/Notifications/NotificationsSideBar')
);

// Settings
const SettingsSideBar = lazy(() => import('../../containers/Dashboard/SettingsSideBar'));
const Dashboard = lazy(() => import('../../containers/Dashboard/Dashboard'));
const NotificationSettings = lazy(() => import('../../containers/Dashboard/NotificationSettings'));
const SupportPane = lazy(() => import('../../containers/Dashboard/panes/SupportPane'));
const EditProfilePane = lazy(() => import('../../containers/Dashboard/panes/EditProfilePane'));
const ChangePasswordPane = lazy(
  () => import('../../containers/Dashboard/panes/ChangePasswordPane')
);
const DeleteAccountPane = lazy(() => import('../../containers/Dashboard/panes/DeleteAccountPane'));
const TwoFactorSettings = lazy(() => import('../../containers/TwoFactor/TwoFactorSettings'));
const LocalPasscode = lazy(() => import('../../containers/localPasscode/LocalPasscode'));

// One-off full-screen surfaces
const ChangePassword = lazy(() => import('../../containers/ChangePassword/ChangePassword'));
const JoinGroup = lazy(() => import('../../containers/JoinGroup/JoinGroup'));

/**
 * Shown while a route chunk is in flight.
 *
 * Centred in the pane rather than as a full-page takeover: the rail and the sidebar are
 * already painted by `ChatShell` at that point, and replacing them would make an
 * in-app navigation flash the whole shell away and back.
 */
const RouteFallback = () => (
  <div className="route-suspense">
    <Loader.Circular size={36} />
  </div>
);

/** Old group links (shared invites, bookmarks) land on the unified route. */
const GroupMessagesRedirect = () => {
  const { groupName } = useParams();
  return <Navigate to={groupName ? `${ROUTES.MESSAGES}/${groupName}` : ROUTES.MESSAGES} replace />;
};

/**
 * Routes are grouped by the sidebar that sits beside them. ChatShell renders
 * the rail, that sidebar, and an <Outlet /> for the pane.
 *
 * Messages is the landing page. Contacts and New Group no longer have pages —
 * both now happen in the new-conversation popup on the messages list.
 *
 * `path` stays a literal — this table is where a route is *declared*. Redirect
 * targets use ROUTES, because those are references to a path owned elsewhere.
 */
const AppRoutes = () => {
  return (
    // One boundary around the whole table rather than one per route: every lazy element
    // below resolves to the same fallback, and nesting boundaries per route would let a
    // parent shell unmount while its child chunk loads.
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to={ROUTES.MESSAGES} replace />} />

        {/* Conversations — direct chats and groups share one route and one
            sidebar; ConversationPane picks the pane for the id. */}
        <Route element={<ChatShell Sidebar={MessagesSideBar} />}>
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:memberName" element={<ConversationPane />} />
        </Route>

        <Route path="/group-messages" element={<Navigate to={ROUTES.MESSAGES} replace />} />
        <Route path="/group-messages/:groupName" element={<GroupMessagesRedirect />} />

        {/* Calls: history in the first pane, the selected call in the second */}
        <Route element={<ChatShell Sidebar={CallsSideBar} />}>
          <Route path="/calls" element={<Navigate to={ROUTES.CALLS} replace />} />
          <Route path="/calls/missed-calls" element={<CallDetail />} />
          <Route path="/calls/recent-calls" element={<CallDetail />} />
        </Route>

        {/* Saved messages: the bookmark list in the sidebar, the selected bookmark in
            the pane. This was a single full-width pane with no sidebar — a bookmark is
            arguably its own detail — but it was split so the section carries the shared
            wordmark header the other sections do, which costs one extra click to reach
            "Open conversation". Selection is shell state, like calls and notifications. */}
        <Route element={<ChatShell Sidebar={SavedSideBar} />}>
          <Route path="/saved" element={<SavedMessages />} />
        </Route>

        {/* Notes: the list in the sidebar, the open document in the pane. `/notes` with
            no id renders the empty pane beside the list, which is what makes the section
            land on something rather than on nothing. */}
        <Route element={<ChatShell Sidebar={NotesSideBar} />}>
          <Route path="/notes" element={<NoteDocument />} />
          <Route path="/notes/:noteId" element={<NoteDocument />} />
        </Route>

        {/* Notification feed */}
        <Route element={<ChatShell Sidebar={NotificationsSideBar} />}>
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notifications/unread-messages" element={<Notifications />} />
          <Route path="/notifications/unread-mentions" element={<Notifications />} />
        </Route>

        {/* Settings: tabs in the first pane, the section in the second */}
        <Route element={<ChatShell Sidebar={SettingsSideBar} />}>
          <Route path="/settings" element={<Navigate to={ROUTES.SETTINGS_PROFILE} replace />} />
          <Route path="/settings/profile" element={<EditProfilePane />} />
          <Route path="/settings/security" element={<Dashboard />} />
          <Route path="/settings/support" element={<SupportPane />} />
          <Route path="/settings/notifications" element={<NotificationSettings />} />

          {/* Detail views render in the same pane, each with a back arrow */}
          <Route
            path="/settings/profile/edit"
            element={<Navigate to={ROUTES.SETTINGS_PROFILE} replace />}
          />
          <Route path="/settings/security/password" element={<ChangePasswordPane />} />
          <Route path="/settings/security/2fa" element={<TwoFactorSettings />} />
          <Route path="/settings/security/passcode" element={<LocalPasscode />} />
          <Route path="/settings/security/delete" element={<DeleteAccountPane />} />
        </Route>

        {/* Old URLs kept working after the settings area was reorganised */}
        <Route path="/profile" element={<Navigate to={ROUTES.SETTINGS_PROFILE} replace />} />
        <Route
          path="/profile/2fa"
          element={<Navigate to={ROUTES.SETTINGS_SECURITY_2FA} replace />}
        />
        <Route
          path="/profile/notifications"
          element={<Navigate to={ROUTES.SETTINGS_NOTIFICATIONS} replace />}
        />
        <Route
          path="/security/2fa"
          element={<Navigate to={ROUTES.SETTINGS_SECURITY_2FA} replace />}
        />
        <Route
          path="/passcode"
          element={<Navigate to={ROUTES.SETTINGS_SECURITY_PASSCODE} replace />}
        />
        <Route path="/contacts" element={<Navigate to={ROUTES.MESSAGES} replace />} />
        <Route path="/new-groups" element={<Navigate to={ROUTES.MESSAGES} replace />} />

        {/* Full-screen surfaces — these own the whole viewport. The call view
            took a `ChatLeftSidebar` it never rendered; passing it made the
            route read as though the call sat inside the shell, which it does
            not.

            The forced password change belongs here rather than in the settings
            group: it is the gate an account with `status === 0` is sent to
            before it can use anything, it renders its own two-column screen
            instead of a pane, and inside the shell that screen was laid out
            beside the settings list — which on a phone put it off-screen with
            no way to reach it. */}
        {/* CallRoute, not CallingAgora — it owns the AgoraRTCProvider, so the RTC SDK
            arrives with this chunk instead of in the initial bundle.

            Two paths, one component. `/calling/:id` is where a call lives: the id in
            the URL is what lets a reloaded tab — or a notification click on a cold
            start — fetch its way back into the call, which neither Redux nor
            sessionStorage could be trusted to survive.

            The bare `/calling` stays routable as a legacy alias. `src/index.js`
            registers the service worker without skipWaiting/clients.claim, so after a
            deploy the OLD worker keeps sending bare `/calling` as its notification
            click target until every tab has closed. It resolves to the live call's id
            or redirects out — see the effects in useCallSession. */}
        <Route path="/calling/:id" element={<CallRoute />} />
        <Route path="/calling" element={<CallRoute />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/join-groups/:id?/:name?/:type?" element={<JoinGroup />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
